// src/services/task.service.ts
import { db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import {
  Task,
  TaskStatus,
  TaskPriority,
  TaskType,
  TaskFilter,
  TaskStats
} from '../types/task.types';

export const taskService = {
  async createTask(
    taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const taskRef = doc(collection(db, 'tasks'));
    const task: Task = {
      ...taskData,
      id: taskRef.id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    await setDoc(taskRef, task);
    return taskRef.id;
  },

  async getTask(taskId: string): Promise<Task | null> {
    const docRef = doc(db, 'tasks', taskId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as Task;
    }
    return null;
  },

  async getTasks(filters?: TaskFilter): Promise<Task[]> {
    let q = query(collection(db, 'tasks'), orderBy('dueDate', 'asc'));

    if (filters?.assignedTo) {
      q = query(q, where('assignedTo', '==', filters.assignedTo));
    }

    if (filters?.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }

    const snapshot = await getDocs(q);
    let tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task));

    // Apply additional filters in memory
    if (filters?.status && filters.status.length > 0) {
      tasks = tasks.filter(t => filters.status!.includes(t.status));
    }

    if (filters?.priority && filters.priority.length > 0) {
      tasks = tasks.filter(t => filters.priority!.includes(t.priority));
    }

    if (filters?.type && filters.type.length > 0) {
      tasks = tasks.filter(t => filters.type!.includes(t.type));
    }

    if (filters?.overdue) {
      const now = new Date();
      tasks = tasks.filter(t =>
        t.status !== 'completed' &&
        t.dueDate.toDate() < now
      );
    }

    return tasks;
  },

  async getUserTasks(userId: string, includeCompleted = false): Promise<Task[]> {
    let q = query(
      collection(db, 'tasks'),
      where('assignedTo', '==', userId),
      orderBy('dueDate', 'asc')
    );

    const snapshot = await getDocs(q);
    let tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task));

    if (!includeCompleted) {
      tasks = tasks.filter(t => t.status !== 'completed');
    }

    return tasks;
  },

  async updateTaskStatus(
    taskId: string,
    status: TaskStatus,
    completionNotes?: string
  ): Promise<void> {
    const updates: Partial<Task> = {
      status,
      updatedAt: Timestamp.now()
    };

    if (status === 'completed') {
      updates.completedAt = Timestamp.now();
      if (completionNotes) {
        updates.completionNotes = completionNotes;
      }
    }

    await updateDoc(doc(db, 'tasks', taskId), updates);
  },

  async updateTask(taskId: string, updates: Partial<Task>): Promise<void> {
    await updateDoc(doc(db, 'tasks', taskId), {
      ...updates,
      updatedAt: Timestamp.now()
    });
  },

  async deleteTask(taskId: string): Promise<void> {
    await deleteDoc(doc(db, 'tasks', taskId));
  },

  async getTaskStats(filters?: { userId?: string; projectId?: string; organizationId?: string }): Promise<TaskStats> {
    let q = query(collection(db, 'tasks'));

    if (filters?.userId) {
      q = query(q, where('assignedTo', '==', filters.userId));
    }

    if (filters?.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }

    if (filters?.organizationId) {
      q = query(q, where('organizationId', '==', filters.organizationId));
    }

    const snapshot = await getDocs(q);
    const tasks = snapshot.docs.map(doc => doc.data() as Task);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));

    const stats: TaskStats = {
      total: tasks.length,
      pending: tasks.filter(t => t.status === 'pending').length,
      inProgress: tasks.filter(t => t.status === 'in_progress').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: tasks.filter(t =>
        t.status !== 'completed' &&
        t.dueDate.toDate() < now
      ).length,
      dueToday: tasks.filter(t => {
        const dueDate = t.dueDate.toDate();
        return dueDate >= today && dueDate < tomorrow && t.status !== 'completed';
      }).length,
      dueTomorrow: tasks.filter(t => {
        const dueDate = t.dueDate.toDate();
        const nextDay = new Date(tomorrow);
        nextDay.setDate(nextDay.getDate() + 1);
        return dueDate >= tomorrow && dueDate < nextDay && t.status !== 'completed';
      }).length,
      dueThisWeek: tasks.filter(t => {
        const dueDate = t.dueDate.toDate();
        return dueDate >= today && dueDate <= endOfWeek && t.status !== 'completed';
      }).length
    };

    return stats;
  },

  async batchCompleteTasks(taskIds: string[]): Promise<void> {
    const batch = writeBatch(db);

    for (const taskId of taskIds) {
      const taskRef = doc(db, 'tasks', taskId);
      batch.update(taskRef, {
        status: 'completed',
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }

    await batch.commit();
  },

  async getUrgentTasks(userId: string, limit = 5, projectId?: string): Promise<Task[]> {
    const tasks = await this.getUserTasks(userId, false);
    const now = new Date();
    // Surface anything due within the next 2 days (covers lead-followup tasks
    // auto-created with a 2-business-day SLA), plus anything urgent regardless
    // of due date.
    const horizon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2, 23, 59, 59);

    return tasks
      // Scope to the active project when one is selected, so tasks from other
      // projects don't leak onto the dashboard.
      .filter(t => !projectId || t.projectId === projectId)
      .filter(t =>
        t.dueDate.toDate() <= horizon ||
        t.priority === 'urgent'
      )
      .slice(0, limit);
  },

  // Create lead follow-up task for client_user
  async createLeadFollowUpTask(data: {
    organizationId: string;
    projectId: string;
    projectName: string;
    leadId: string;
    leadName: string;
    assignedTo: string;
    assignedToName: string;
    createdBy: string;
    createdByName: string;
  }): Promise<string> {
    // Due in 2 business days
    const dueDate = new Date();
    let daysAdded = 0;
    while (daysAdded < 2) {
      dueDate.setDate(dueDate.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dueDate.getDay() !== 0 && dueDate.getDay() !== 6) {
        daysAdded++;
      }
    }

    const taskRef = doc(collection(db, 'tasks'));
    const task: Task = {
      id: taskRef.id,
      organizationId: data.organizationId,
      projectId: data.projectId,
      projectName: data.projectName,
      type: 'lead_followup',
      title: `Follow up with new lead: ${data.leadName}`,
      description: `A new lead has been added. Please reach out to ${data.leadName} to:\n• Confirm event time and address\n• Answer any early questions\n• Introduce yourself and your services`,
      status: 'pending',
      priority: 'high',
      assignedTo: data.assignedTo,
      assignedToName: data.assignedToName,
      assignedBy: data.createdBy,
      assignedByName: data.createdByName,
      leadId: data.leadId,
      leadName: data.leadName,
      dueDate: Timestamp.fromDate(dueDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      autoGenerated: true,
      source: 'lead_assignment'
    };

    await setDoc(taskRef, task);
    return taskRef.id;
  },

  // Create tour reminder task for canvasser
  async createTourReminderTask(data: {
    organizationId: string;
    projectId: string;
    projectName: string;
    leadId: string;
    leadName: string;
    appointmentDate: string;
    appointmentTime: string;
    eventType: 'tour' | 'phone' | 'meeting';
    assignedTo: string;
    assignedToName: string;
  }): Promise<string> {
    // Due today (day before the appointment)
    const dueDate = new Date();
    dueDate.setHours(17, 0, 0, 0); // Due by 5 PM

    const eventTypeLabel = data.eventType === 'tour' ? 'Tour' : data.eventType === 'phone' ? 'Phone Call' : 'Meeting';

    const taskRef = doc(collection(db, 'tasks'));
    const task: Task = {
      id: taskRef.id,
      organizationId: data.organizationId,
      projectId: data.projectId,
      projectName: data.projectName,
      type: 'tour_reminder',
      title: `Confirm ${eventTypeLabel} with ${data.leadName}`,
      description: `Reminder: ${data.leadName} has a ${eventTypeLabel.toLowerCase()} scheduled for tomorrow (${data.appointmentDate} at ${data.appointmentTime}).\n\nPlease:\n• Call to confirm attendance\n• Answer any last-minute questions\n• Provide directions or instructions if needed`,
      status: 'pending',
      priority: 'high',
      assignedTo: data.assignedTo,
      assignedToName: data.assignedToName,
      leadId: data.leadId,
      leadName: data.leadName,
      dueDate: Timestamp.fromDate(dueDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      autoGenerated: true,
      source: 'tour_reminder'
    };

    await setDoc(taskRef, task);
    return taskRef.id;
  },

  // Create lead no-contact notification task for provider_user
  async createLeadNoContactTask(data: {
    organizationId: string;
    projectId: string;
    projectName: string;
    leadId: string;
    leadName: string;
    clientUserId: string;
    clientUserName: string;
    daysSinceLastContact: number;
    assignedTo: string; // provider_user to notify
    assignedToName: string;
  }): Promise<string> {
    // Due today
    const dueDate = new Date();
    dueDate.setHours(17, 0, 0, 0); // Due by 5 PM

    const taskRef = doc(collection(db, 'tasks'));
    const task: Task = {
      id: taskRef.id,
      organizationId: data.organizationId,
      projectId: data.projectId,
      projectName: data.projectName,
      type: 'lead_no_contact',
      title: `Lead Not Contacted: ${data.leadName}`,
      description: `Alert: ${data.leadName} has not been contacted in ${data.daysSinceLastContact} days.\n\nAssigned to: ${data.clientUserName}\n\nPlease follow up to ensure the lead is being properly managed.`,
      status: 'pending',
      priority: 'high',
      assignedTo: data.assignedTo,
      assignedToName: data.assignedToName,
      leadId: data.leadId,
      leadName: data.leadName,
      dueDate: Timestamp.fromDate(dueDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      autoGenerated: true,
      source: 'lead_no_contact'
    };

    await setDoc(taskRef, task);
    return taskRef.id;
  },

  // Check for existing tour reminder task to avoid duplicates
  async hasTourReminderTask(leadId: string, appointmentDate: string): Promise<boolean> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, 'tasks'),
      where('leadId', '==', leadId),
      where('type', '==', 'tour_reminder'),
      where('source', '==', 'tour_reminder')
    );

    const snapshot = await getDocs(q);
    // Check if any task was created today for this lead
    return snapshot.docs.some(doc => {
      const task = doc.data() as Task;
      const createdAt = task.createdAt.toDate();
      createdAt.setHours(0, 0, 0, 0);
      return createdAt.getTime() === today.getTime();
    });
  },

  // Check for existing no-contact task to avoid duplicates
  async hasNoContactTask(leadId: string): Promise<boolean> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(
      collection(db, 'tasks'),
      where('leadId', '==', leadId),
      where('type', '==', 'lead_no_contact'),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty;
  },

  // Create the next occurrence of a recurring task
  async createNextRecurrence(task: Task): Promise<string | null> {
    if (!task.recurrence) return null;

    const { frequency, interval, endDate } = task.recurrence;
    const currentDueDate = task.dueDate.toDate();
    const nextDueDate = new Date(currentDueDate);

    // Calculate next due date based on frequency
    switch (frequency) {
      case 'daily':
        nextDueDate.setDate(nextDueDate.getDate() + interval);
        break;
      case 'weekly':
        nextDueDate.setDate(nextDueDate.getDate() + (interval * 7));
        break;
      case 'monthly':
        nextDueDate.setMonth(nextDueDate.getMonth() + interval);
        break;
    }

    // Check if next occurrence is past the end date
    if (endDate && nextDueDate > endDate.toDate()) {
      return null;
    }

    // Create the next task
    const taskRef = doc(collection(db, 'tasks'));
    const nextTask: Task = {
      ...task,
      id: taskRef.id,
      status: 'pending',
      dueDate: Timestamp.fromDate(nextDueDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedAt: undefined,
      completionNotes: undefined,
      parentTaskId: task.parentTaskId || task.id // Link to original task
    };

    await setDoc(taskRef, nextTask);
    return taskRef.id;
  },

  // Complete a recurring task and create next occurrence
  async completeRecurringTask(
    taskId: string,
    completionNotes?: string
  ): Promise<{ nextTaskId: string | null }> {
    const task = await this.getTask(taskId);
    if (!task) throw new Error('Task not found');

    // Update the current task as completed
    await this.updateTaskStatus(taskId, 'completed', completionNotes);

    // Create next occurrence if it's a recurring task
    let nextTaskId: string | null = null;
    if (task.recurrence) {
      nextTaskId = await this.createNextRecurrence(task);
    }

    return { nextTaskId };
  }
};
